// The moderation ladder's screens (ai_app_backend #1681, item 5): repeated refused requests bring a warning, then
// pauses of an hour, a day and a week, then a hold on the account. v2 decides every step and answers where the person
// stands as `GET /v2/me`'s `moderation`; this file only turns that answer into which notice the site shows, and the
// answer to "I agree to the content rules" (`POST /v2/me/rules-agreement`) into what happens next. Nothing is decided
// here: no count, no time limit, no rule.
//
// The default is today's site: while the ladder is switched off v2 answers `moderation: null`, and `none` means
// nothing is held against the person; both show exactly what the site showed before the ladder (no notice at all).
import type { components } from './api/v2-contract'

/** v2's word on where the person stands on the ladder; null while the ladder is switched off. */
export type Moderation = components['schemas']['Moderation']

/**
 * Which notice the site shows: `today` is the site as it was before the ladder (nothing shown); `warning` a gentle,
 * dismissible notice; `paused` making and publishing paused until a time; `agree` the hold screen with the "I agree"
 * button; `support` the hold only support can lift, with no button.
 */
export type LadderView = 'today' | 'warning' | 'paused' | 'agree' | 'support'

/** The notice for v2's answer. Anything v2 doesn't name here (null, `none`, a state added later) is today's site. */
export function ladderView(
  moderation: Moderation | null | undefined,
): LadderView {
  switch (moderation?.state) {
    case 'warning':
      return 'warning'
    case 'paused':
      return 'paused'
    case 'held':
      // v2: call the agreement only when `lifted_by` is `agreeing`; every other hold is support's to lift.
      return moderation.hold === 'agree' && moderation.lifted_by === 'agreeing'
        ? 'agree'
        : 'support'
    default:
      return 'today'
  }
}

/** The safeStorage key that remembers the day the warning was put away. */
export const WARNING_DISMISSED_KEY = 'moderationWarningDismissed'

/** The person's own calendar day, `YYYY-MM-DD` in local time: a warning put away comes back the next day. */
export function localDay(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Whether the warning was put away today (`stored` is what safeStorage gave back, null when nothing or blocked). */
export function warningDismissedToday(
  stored: string | null,
  now: Date,
): boolean {
  return stored === localDay(now)
}

/** When a pause ends, in the person's language and time zone; null when v2 gave no time (or one that can't be read). */
export function pausedUntilText(
  pausedUntil: string | null | undefined,
  locale?: string,
  timeZone?: string,
): string | null {
  if (!pausedUntil) return null
  const at = new Date(pausedUntil)
  if (Number.isNaN(at.getTime())) return null
  const style = { dateStyle: 'medium', timeStyle: 'short', timeZone } as const
  try {
    return at.toLocaleString(locale, style)
  } catch {
    // An unknown locale tag: the browser's own.
    return at.toLocaleString(undefined, style)
  }
}

/**
 * What the hold screen does with v2's answer to "I agree to the content rules":
 * - `refresh`: 200, where they stand now (`state` `none` once lifted): read the account again;
 * - `support`: 403 `hold_needs_support`, only support can lift this hold: show the support view;
 * - `today`: 404 `not_found`, the ladder is switched off: show today's site;
 * - `failed`: anything else (no answer, a 5xx, banned, deleting …): say it didn't work and read the account again.
 */
export type AgreeOutcome =
  | { kind: 'refresh'; moderation: Moderation }
  | { kind: 'support' }
  | { kind: 'today' }
  | { kind: 'failed' }

/** The outcome of the agreement's answer (its status and JSON body; a body that can't be read is none). */
export async function agreeOutcome(
  response: Response | null | undefined,
): Promise<AgreeOutcome> {
  if (!response) return { kind: 'failed' }
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    body = null
  }
  const code = (body as { code?: unknown } | null)?.code
  if (response.status === 200 && body && typeof body === 'object')
    return { kind: 'refresh', moderation: body as Moderation }
  if (response.status === 403 && code === 'hold_needs_support')
    return { kind: 'support' }
  if (response.status === 404) return { kind: 'today' }
  return { kind: 'failed' }
}
